/**
 * Bounds checks for the app's share fields — the numbers that express "a
 * portion of something" and therefore have a hard ceiling.
 *
 * Every such column in the DB is stored as a 0–1 decimal fraction
 * (numeric(10,8)), but the UI presents two different scales, and that
 * difference is deliberate (it follows land convention):
 *
 *   - INTEREST fractions of 8/8ths — delivered_nri, nri, wi, allocation,
 *     lessor_royalty, other_burdens, warwick_orri — are entered and displayed
 *     as decimals at 8 dp (0.80123450). Ceiling: 1.
 *   - The deal-split percentage — deal_entities.pct_deal_receiving — is
 *     entered and displayed as a percent (60 for 60%), converted ×100/÷100 at
 *     the EntitiesTab boundary. Ceiling: 100.
 *
 * Either way the value can never exceed its whole. `DATA_QUALITY_RULES`
 * (utils/dealMetrics.js) flags rows already stored over the line; these rules
 * stop new ones being saved.
 */

/** Ceiling for an interest fraction of 8/8ths. */
export const MAX_INTEREST = 1;
/** Ceiling for a percent-scaled share. */
export const MAX_PERCENT = 100;

const buildError = (value, { label, max, unit = "", hint }) => {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(String(value).trim());
  if (!Number.isFinite(n)) return `${label} must be a number.`;
  if (n < 0) return `${label} cannot be negative.`;
  if (n > max) {
    return `${label} cannot exceed ${max}${unit}${hint ? ` — ${hint}` : ""}.`;
  }
  return null;
};

/**
 * Validate an interest fraction (0–1). Blank is valid — these columns are
 * nullable, and "required" is a separate metadata-driven rule.
 *
 * @param {*} value
 * @param {string} [label]
 * @returns {string|null}
 */
export const interestBoundsError = (value, label = "Delivered NRI") =>
  buildError(value, {
    label,
    max: MAX_INTEREST,
    hint: "it is a decimal fraction of 8/8ths, not a percent. Enter 0.80123450, not 80",
  });

/**
 * Validate a percent-scaled share (0–100) — one row of a deal split.
 *
 * @param {*} value
 * @param {string} [label]
 * @returns {string|null}
 */
export const pctBoundsError = (value, label = "% Deal Receiving") =>
  buildError(value, {
    label,
    max: MAX_PERCENT,
    unit: "%",
    hint: "a single row cannot take more than the whole deal",
  });

/**
 * Wrap either check as an AntD `Form.Item` rule, so `form.validateFields()`
 * rejects and the save handler bails out before any request is fired.
 *
 * @param {(value: unknown, label?: string) => string|null} check
 * @param {string} [label]
 */
export const boundsRule = (check, label) => ({
  validator: (_rule, value) => {
    const err = check(value, label);
    return err ? Promise.reject(new Error(err)) : Promise.resolve();
  },
});

export const interestBoundsRule = (label) => boundsRule(interestBoundsError, label);
export const pctBoundsRule = (label) => boundsRule(pctBoundsError, label);

// Absorbs float noise when summing percents that were round-tripped through
// 0–1 fractions (0.1 + 0.2 style drift), matching EntitiesTab's tolerance.
const SUM_TOLERANCE = 1e-4;

const trim = (n) => Number(n.toFixed(6));

/**
 * Would saving this row push its side's split past 100%?
 *
 * Over-allocation is always wrong, so it blocks the save. UNDER-allocation is
 * not an error — you cannot reach 100% while entering the first buyer — so it
 * stays a warning banner on the tab.
 *
 * @param {object}   args
 * @param {number}   args.othersTotal — summed percent of the OTHER rows on this side
 * @param {*}        args.value       — the percent being saved for this row
 * @param {string}   args.sideTitle   — "Buyers" / "Sellers"
 * @param {string}   args.label       — "% Deal Receiving" / "% Deal Contributing"
 * @returns {string|null}
 */
export const overAllocationError = ({ othersTotal, value, sideTitle, label }) => {
  const others = Number(othersTotal) || 0;
  const n = Number(value) || 0;
  const total = others + n;
  if (total <= MAX_PERCENT + SUM_TOLERANCE) return null;
  const headroom = Math.max(0, trim(MAX_PERCENT - others));
  return (
    `${sideTitle} ${label} would total ${trim(total)}% — over 100%. ` +
    `${headroom}% is unallocated, so this row can be at most ${headroom}%.`
  );
};

export default interestBoundsError;
